import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface AddToGroupActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}

export const AddToGroupActionSchema = z
  .object({
    type: z.literal('Add_To_Group'),
    groupEmail: z.array(z.string()).min(1, 'Group is required.'),
    groupRole: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    // This is not visible validation error
    if (data.groupEmail.length !== data.groupRole.length) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Internal state mismatch: group properties have different lengths.',
      });
    }
  });
export type AddToGroupActionType = z.infer<typeof AddToGroupActionSchema>;

// Type guard to check if an action is AddToGroupActionType
function isAddToGroupAction(
  action: PolicyAction
): action is AddToGroupActionType {
  return action.type === 'Add_To_Group';
}

export default function AddToGroupAction({
  setValue,
  watch,
  errors,
  control,
  trigger,
}: AddToGroupActionProps) {
  const actionData = watch('actionData');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const idx = actionData.map((action) => action.type).indexOf('Add_To_Group');
  const actionAtIndex = actionData[idx];
  if (!isAddToGroupAction(actionAtIndex)) {
    return null;
  }

  const currentAction: AddToGroupActionType = actionAtIndex;
  const error: FieldErrors<AddToGroupActionType> | undefined =
    errors?.actionData?.[idx];

  function removeSelection(email: string, index: number) {
    const newList = currentAction.groupEmail.filter(
      (groupEmail) => groupEmail !== email
    );
    const newRoles = currentAction.groupRole.filter((_, i) => i !== index);

    setValue(`actionData.${idx}.groupEmail`, [...newList]);
    setValue(`actionData.${idx}.groupRole`, newRoles);
  }

  const memberRoleOptions = [
    { displayValue: 'Member', value: 'MEMBER' },
    { displayValue: 'Manager', value: 'MANAGER' },
    { displayValue: 'Owner', value: 'OWNER' },
  ];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Stack flexDirection={'row'} gap={1}>
          <Box sx={{ width: 400 }}>
            <Controller
              name={`actionData.${idx}.groupEmail`}
              control={control}
              render={({ field }) => (
                <GroupsAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={(value) => {
                    if (!value) {
                      setAlreadySelectedError(false);
                      return;
                    }
                    if ((currentAction.groupEmail || []).includes(value)) {
                      setAlreadySelectedError(true);
                      return;
                    }

                    setAlreadySelectedError(false);
                    const updatedGroupEmails = [
                      ...(currentAction.groupEmail || []),
                      value,
                    ];
                    field.onChange(updatedGroupEmails);

                    const newlyAddedIndex = updatedGroupEmails.length - 1;
                    setValue(
                      `actionData.${idx}.groupRole.${newlyAddedIndex}`,
                      'MEMBER'
                    );

                    autocompleteKey.current = Date.now().toString();
                    setAutoCompleteResponse('');

                    trigger(`actionData.${idx}.groupEmail`);
                  }}
                  errors={!!error?.groupEmail || alreadySelectedError}
                  helperText={
                    alreadySelectedError
                      ? 'Group is already selected.'
                      : error?.groupEmail
                      ? error?.groupEmail?.message
                      : ' '
                  }
                />
              )}
            />
          </Box>
        </Stack>
      </Stack>
      <Box sx={{ maxHeight: 200 }}>
        <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 200 }}>
          <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
            <Table size={'small'} stickyHeader aria-label="searched-groups">
              <TableHead>
                <TableRow>
                  <TableCell>Selected Groups</TableCell>
                  <TableCell>Group Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentAction.groupEmail
                  ? currentAction.groupEmail.map((group, index) => (
                      <TableRow key={group}>
                        <TableCell>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2">{group}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <TextField
                              inputProps={{
                                id: 'memberRole',
                              }}
                              InputLabelProps={{
                                htmlFor: 'memberRole',
                              }}
                              label="Member Role"
                              value={currentAction.groupRole[index]}
                              onChange={(event) => {
                                const newRoles = [...currentAction.groupRole];
                                newRoles[index] = event.target.value;
                                setValue(
                                  `actionData.${idx}.groupRole`,
                                  newRoles
                                );
                              }}
                              autoComplete="off"
                              select
                              sx={{ width: 115 }}
                            >
                              {memberRoleOptions.map((option) => (
                                <MenuItem
                                  value={option.value}
                                  key={option.displayValue}
                                >
                                  {option.displayValue}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Tooltip arrow title="Delete">
                              <span>
                                <IconButton
                                  onClick={() => {
                                    removeSelection(
                                      currentAction.groupEmail[index],
                                      index
                                    );
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Stack>
  );
}
